<script lang="ts">
    import { onMount, tick } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import { auth } from '../../framework/js/auth.js';
    import { toast } from '../../framework/js/toast.js';
    import { push } from 'svelte-spa-router';
    import {
        AuthLayout,
        AuthPasswordInput,
        AuthPrimaryButton,
        AuthStatusCallout,
        AppLoadingState,
    } from '../../framework/components/app';

    export let params: { token?: string } = {};

    type Status = 'loading' | 'accepting' | 'success' | 'error' | 'login-required' | 'register';
    let status: Status = 'loading';
    let message = '';
    let inviteInfo: any = null;

    let firstNameEl: HTMLInputElement;
    let firstName = '';
    let lastName = '';
    let mobile = '';
    let password = '';
    let c_password = '';
    let errors: Record<string, string> = {};
    let isSubmitting = false;

    onMount(async () => {
        const token = params.token;
        if (!token) { status = 'error'; message = 'Invalid invitation link.'; return; }

        const infoRes: any = await api.getTeamInvitationInfo(token);
        if (!infoRes.success) {
            status = 'error';
            message = infoRes.message || 'Invalid or expired invitation.';
            return;
        }
        inviteInfo = infoRes.data;

        if (!$auth?.token) {
            localStorage.setItem('pending_team_invite', token);
            status = 'login-required';
            return;
        }
        await acceptInvitation(token);
    });

    async function acceptInvitation(token: string) {
        status = 'accepting';
        const res: any = await api.acceptTeamInvitation(token);
        if (res.success) {
            status = 'success';
            localStorage.removeItem('pending_team_invite');
            if (res.data?.team_uuid) {
                auth.update((state: any) => {
                    if (state.user) state.user.active_team_uuid = res.data.team_uuid;
                    return state;
                });
            }
            toast.showNotification(true, `You've joined ${inviteInfo?.team_name || 'the team'}!`);
            setTimeout(() => push('/team'), 2000);
        } else {
            status = 'error';
            message = res.message || 'Failed to accept invitation.';
        }
    }

    function goToLogin() {
        localStorage.setItem('prefill_email', inviteInfo.email);
        push('/login');
    }

    async function showRegisterForm() {
        status = 'register';
        await tick();
        firstNameEl?.focus();
    }

    function validateRegisterForm() {
        errors = {};
        let valid = true;
        if (!firstName.trim()) { errors.firstName = 'First name is required'; valid = false; }
        if (!lastName.trim()) { errors.lastName = 'Last name is required'; valid = false; }
        if (!password) { errors.password = 'Password is required'; valid = false; }
        else if (password.length < 6) { errors.password = 'Password must be at least 6 characters'; valid = false; }
        if (!c_password) { errors.c_password = 'Please confirm your password'; valid = false; }
        else if (password !== c_password) { errors.c_password = 'Passwords do not match'; valid = false; }
        return valid;
    }

    async function handleRegister() {
        if (!validateRegisterForm()) return;
        const token = params.token;
        if (!token) {
            toast.showNotification(false, 'Invalid invitation link.', 'top');
            return;
        }
        isSubmitting = true;
        let res: any;
        try {
            res = await api.registerViaInvitation(token, {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                mobile: mobile.trim() || null,
                password,
                c_password,
            });
        } catch {
            toast.showNotification(false, 'Network error. Please try again.', 'top');
            isSubmitting = false;
            return;
        }

        if (res.success) {
            localStorage.setItem('token', res.data.token);
            auth.update((state: any) => {
                state.token = res.data.token;
                state.user = res.data.user;
                return state;
            });
            localStorage.removeItem('pending_team_invite');
            toast.showNotification(true, `Welcome! You've joined ${res.data.team_name || 'the team'}.`);
            await api.getResourceLists();
            push('/dashboard');
        } else {
            toast.showNotification(false, res.message || 'Registration failed. Please try again.', 'top');
            isSubmitting = false;
        }
    }

    $: if (errors.firstName && firstName) errors.firstName = '';
    $: if (errors.lastName && lastName) errors.lastName = '';
    $: if (errors.password && password && password.length >= 6) errors.password = '';
    $: if (errors.c_password && c_password && password === c_password) errors.c_password = '';
    $: passwordsMismatch = c_password.length > 0 && password !== c_password;
</script>

{#if status === 'loading' || status === 'accepting'}
    <AuthLayout
        title={status === 'accepting' ? 'Joining team…' : 'Loading invitation…'}
        subtitle="Please wait."
        showMarketingLink={false}
    >
        <AppLoadingState label={status === 'accepting' ? 'Accepting invitation…' : 'Loading invitation…'} />
    </AuthLayout>
{:else if status === 'success'}
    <AuthLayout
        title={`Welcome to ${inviteInfo?.team_name || 'the team'}!`}
        subtitle="You've successfully joined the team. Redirecting…"
        showMarketingLink={false}
    >
        <AuthStatusCallout tone="success" icon="check circle" title="You're in">
            <p>Taking you to your team workspace now.</p>
        </AuthStatusCallout>
    </AuthLayout>
{:else if status === 'login-required' && inviteInfo}
    <AuthLayout title="Team invitation" subtitle="Review the details below, then continue." showMarketingLink={false}>
        <div class="invite-details">
            {#if inviteInfo.invited_by}
                <p class="invite-detail"><strong>{inviteInfo.invited_by}</strong> has invited you to join</p>
            {:else}
                <p class="invite-detail">You've been invited to join</p>
            {/if}
            <p class="invite-team">{inviteInfo.team_name}</p>
            <p class="invite-email">{inviteInfo.email}</p>
        </div>

        {#if inviteInfo.has_account}
            <AuthStatusCallout tone="info" icon="sign in" title="Sign in to accept">
                <p>Sign in as <strong>{inviteInfo.email}</strong> to accept this invitation.</p>
            </AuthStatusCallout>
            <AuthPrimaryButton type="button" on:click={goToLogin}>Sign in</AuthPrimaryButton>
        {:else}
            <AuthStatusCallout tone="info" icon="user plus" title="Create your account">
                <p>Create an account with <strong>{inviteInfo.email}</strong> to accept this invitation.</p>
            </AuthStatusCallout>
            <AuthPrimaryButton type="button" on:click={showRegisterForm}>Create account</AuthPrimaryButton>
        {/if}
    </AuthLayout>
{:else if status === 'register' && inviteInfo}
    <AuthLayout title="Create your account" subtitle={`Joining ${inviteInfo.team_name} as ${inviteInfo.email}`} showMarketingLink={false}>
        <form class="auth-form" on:submit|preventDefault={handleRegister} novalidate>
            <div class="auth-form-row">
                <div class="auth-form-field" class:error={!!errors.firstName}>
                    <label class="auth-form-label" for="first-name">First name</label>
                    <input bind:this={firstNameEl} id="first-name" class="auth-form-input" type="text" autocomplete="given-name" bind:value={firstName} placeholder="First name" />
                    {#if errors.firstName}<span class="auth-error-message" role="alert">{errors.firstName}</span>{/if}
                </div>
                <div class="auth-form-field" class:error={!!errors.lastName}>
                    <label class="auth-form-label" for="last-name">Last name</label>
                    <input id="last-name" class="auth-form-input" type="text" autocomplete="family-name" bind:value={lastName} placeholder="Last name" />
                    {#if errors.lastName}<span class="auth-error-message" role="alert">{errors.lastName}</span>{/if}
                </div>
            </div>

            <div class="auth-form-field">
                <label class="auth-form-label" for="email">Email</label>
                <input id="email" class="auth-form-input" type="email" value={inviteInfo.email} disabled readonly aria-disabled="true" />
            </div>

            <div class="auth-form-field">
                <label class="auth-form-label" for="mobile">Mobile <span class="field-optional">(optional)</span></label>
                <input id="mobile" class="auth-form-input" type="tel" autocomplete="tel" bind:value={mobile} placeholder="Your mobile number" />
            </div>

            <AuthPasswordInput
                id="password"
                label="Password"
                bind:value={password}
                placeholder="Create a password"
                autocomplete="new-password"
                error={errors.password}
            />

            <AuthPasswordInput
                id="c_password"
                label="Confirm password"
                bind:value={c_password}
                placeholder="Confirm your password"
                autocomplete="new-password"
                error={errors.c_password || (passwordsMismatch ? 'Passwords do not match' : '')}
            />

            <AuthPrimaryButton loading={isSubmitting} loadingLabel="Creating account…">
                Create account &amp; join team
            </AuthPrimaryButton>
        </form>
    </AuthLayout>
{:else}
    <AuthLayout title="Invitation error" subtitle={message} showMarketingLink={false}>
        <AuthStatusCallout tone="warn" icon="exclamation circle" title="Unable to continue">
            <p>{message}</p>
        </AuthStatusCallout>
        <AuthPrimaryButton type="button" on:click={() => push('/login')}>Go to sign in</AuthPrimaryButton>
    </AuthLayout>
{/if}

<style>
    .invite-details {
        background: var(--surface-hover);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        padding: var(--space-4);
        margin-bottom: var(--space-4);
    }

    .invite-detail {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
        margin: 0;
        line-height: 1.5;
    }

    .invite-team {
        font-size: var(--text-xl);
        font-weight: 600;
        color: var(--grey);
        margin: var(--space-1) 0;
        line-height: 1.3;
    }

    .invite-email {
        font-size: var(--text-sm);
        color: var(--primary);
        margin: var(--space-2) 0 0 0;
        font-weight: 500;
    }

    .field-optional {
        color: var(--grey-secondary);
        font-weight: 400;
    }

    @media (max-width: 480px) {
        .auth-form-row {
            flex-direction: column;
        }
    }
</style>
